<form [formGroup]="submissionDialogForm" novalidate>
  <wcg-dialog size="large" [title]="$title()" [description]="data.description">
    <!---------------Dialog Content Container--------------->
    <div class="w-full flex p-4 submission-type-dialog__container" content>
      @defer (
        when data.documentTypes() && data.submissionDocument() && data.attributes()  && $documentTypesArray()
      ) {
        @if (data.tabs.length) {
          <mat-tab-group
            class="w-full"
            animationDuration="0ms"
            (selectedTabChange)="tabChange($event)"
          >
            @for (tab of data.tabs; track tab) {
              <mat-tab class="w-1/2">
                <ng-template mat-tab-label>
                  @if (tab.templateName === "documentTypes") {
                    <p
                      class="submission-type-dialog__tab-name"
                      [matBadge]="$documentTypesArray().length"
                      matBadgeOverlap="false"
                    >
                      {{ tab.name }}
                    </p>
                  } @else if (tab.templateName === "irisSettings") {
                    <p>{{ tab.name }}</p>
                  }
                </ng-template>
                <ng-container
                  [ngTemplateOutlet]="
                    tab.templateName === 'documentTypes'
                      ? documentTypesView
                      : irisSettingsView
                  "
                />
              </mat-tab>
            }
          </mat-tab-group>
        }
      } @placeholder {
        <p>Nothing is happening</p>
      }
    </div>
    <!------------/Dialog Content Container------------>

    <!------------------Dialog Footer----------------->
    <div tertiary>
      <wcg-button
        color="tertiary"
        data-testid="delete-dialog__cancel"
        (click)="cancel()"
      >
        Cancel
      </wcg-button>
    </div>

    @let isDisabled =
      ($currentTab() === "documentType" &&
        $documentTypesArray().length <= 0 &&
        $documentTypesArray().valid) ||
      !($documentTypesArray().length > 0 && $documentTypesArray().valid) ||
      ($currentTab() === "irisSettings" &&
        !submissionDialogForm.controls.irisSettings.valid);

    <div primary>
      <wcg-button
        data-testid="delete-dialog__confirm"
        color="primary"
        (click)="save()"
        [isDisabled]="isDisabled"
      >
        Save
      </wcg-button>
    </div>
    <!---------------/Dialog Footer----------------->
  </wcg-dialog>
  <!---------------Document Type View------------->
  <ng-template #documentTypesView>
    <div class="flex flex-col gap-6 p-4">
      <div class="flex items-center justify-between w-full">
        <h4 class="submission-type-dialog__header">Document Types</h4>
        <button
          type="button"
          mat-flat-button
          wcg-button
          [outlined]="true"
          color="tertiary"
          (click)="addDocumentType()"
        >
          <mat-icon fontIcon="add" />
          Add Document Type
        </button>
      </div>
      @if ($documentTypesArray().length > 0) {
        <ng-container [ngTemplateOutlet]="documentTypesTable" />
      } @else {
        <p class="submission-type-dialog__document-type-no-statement">
          No document types added for this submission yet
        </p>
      }
    </div>
  </ng-template>
  <!-----------Document Type View-------------->
  <!-----------IRIS Settings------------------>
  <ng-template #irisSettingsView>
    <div class="flex flex-col p-4 h-60 gap-8">
      <h4 class="submission-type-dialog__header">Iris Settings</h4>
      <div class="flex gap-6">
        <div class="w-1/2">
          <ng-container
            [ngTemplateOutlet]="selectRef"
            [ngTemplateOutletContext]="{
              formControl:
                submissionDialogForm.controls.irisSettings.controls
                  .routingGroup,
              options: data.defaultIrisRoutingGroups(),
              props: {
                label: 'IRIS Routing Group',
                required: true,
              },
            }"
          />
        </div>
        <div class="w-1/2">
          <ng-container
            [ngTemplateOutlet]="inputRef"
            [ngTemplateOutletContext]="{
              formControl:
                submissionDialogForm.controls.irisSettings.controls
                  .envelopeType,
              props: {
                label: 'IRIS Envelope Type',
                required: true,
                errorText: 'IRIS Envelope Type is required',
              },
            }"
          />
        </div>
      </div>
    </div>
  </ng-template>
  <!-------------/IRIS Settings----------->
  <!-------------DocumentTypesTable-------->
  <ng-template #documentTypesTable>
    <div class="submission-type-dialog__documents-table-container">
      <div class="flex items-center gap-4 p-6">
        <h4
          class="w-1/2 relative left-14 submission-type-dialog__document-type-table-header"
        >
          Document Type
        </h4>
        <h4
          class="w-1/2 relative left-8 submission-type-dialog__document-type-table-header"
        >
          Attribute
        </h4>
      </div>
      <!-----------/DocumentTypesTable--------------->
      <!-----------DocumentTypes Form Array--------->
      <ng-container formArrayName="documentTypes">
        <div
          class="flex flex-col submission-type-dialog__document-type-scrollable"
        >
          @for (
            d of $documentTypesArray().controls;
            track d;
            let documentIndex = $index
          ) {
            <div
              class="flex items-center gap-4 px-4 py-10 submission-type-dialog__document-type-container"
              [formGroupName]="documentIndex"
            >
              <mat-icon
                fontIcon="delete_outlined"
                class="cursor-pointer submission-type-dialog__document-type-icon"
                (click)="deleteDocumentType(documentIndex)"
              />
              @let documentText =
                $documentTypesArray().controls[documentIndex].controls
                  .documentType.value?.label ?? "";

              <!-----------------wcg-inline-editor-------------------------->
              <div class="w-2/3">
                <wcg-inline-editor
                  [defaultView]="documentText.length > 0 ? 'View' : 'Edit'"
                  [turnoffInlineEdit]="documentText.length > 0"
                  [currentFormControl]="
                    $documentTypesArray().controls[documentIndex].controls
                      .documentType
                  "
                >
                  <ng-template wcgInlineEditMode>
                    <ng-container
                      [ngTemplateOutlet]="selectRef"
                      [ngTemplateOutletContext]="{
                        formControl:
                          $documentTypesArray().controls[documentIndex].controls
                            .documentType!,
                        options: $documentTypes(),
                        props: {
                          type: 'documentType',
                          errorText: 'DocumentType is required',
                        },
                      }"
                    />
                  </ng-template>
                  <ng-template wcgInlineViewMode>
                    <ng-container
                      [ngTemplateOutlet]="plainText"
                      [ngTemplateOutletContext]="{
                        plainText: documentText,
                      }"
                    />
                  </ng-template>
                </wcg-inline-editor>
              </div>
              <!----------------/wcg-inline-editor------------------->
              <!----------------attribute-form-control--------------->
              <div class="w-2/3">
                <ng-container
                  [ngTemplateOutlet]="selectRef"
                  [ngTemplateOutletContext]="{
                    formControl:
                      $documentTypesArray().controls[documentIndex].controls
                        .attribute,
                    options: $attributes(),
                    props: {
                      type: 'AttributeType',
                      documentId:
                        $documentTypesArray().controls[documentIndex].controls
                          .documentType.value?.id,
                      errorText: 'Attribute is required',
                    },
                  }"
                />
              </div>
              <!----------------/attribute form-control-------------->
            </div>
          }
        </div>
      </ng-container>
      <!--------/DocumentTypes Form Array--------->
    </div>
  </ng-template>
  <!-----------/DocumentTypesTable------------>
  <!-------------------Reusable Templates---------------->
  <ng-template
    #selectRef
    let-formControl="formControl"
    let-options="options"
    let-props="props"
  >
    <div class="h-[55px]">
      <wcg-uni-select
        [formControl]="formControl"
        [options]="options"
        [label]="props?.label || ''"
        [required]="props?.required || false"
        [height]="$inputHeight().Comfortable"
        [errorText]="
          $currentValidationError().length > 0
            ? $currentValidationError()
            : props.errorText
        "
        (valueChange)="
          updateAttributeType(props.type, $event, props.documentId)
        "
      />
    </div>
  </ng-template>
  <ng-template #plainText let-plainText="plainText">
    <p class="cursor-pointer">{{ plainText }}</p>
  </ng-template>
  <ng-template #inputRef let-formControl="formControl" let-props="props">
    <div class="h-[55px]">
    <wcg-input-field
      class="absolute right-0 w-1/2"
      [formControl]="formControl"
      [label]="props?.label"
      [errorText]="props.errorText"
      [height]="$inputHeight().Comfortable"
      [isRequired]="props?.required || false"
    ></wcg-input-field>
    </div>
  </ng-template>
  <!------------------/Reusable Templates------------->
</form>
